import React from 'react'
import { Link } from 'react-router-dom'
import { Button, Container, Typography } from '@material-ui/core'
import { createStyles, Theme, makeStyles } from '@material-ui/core/styles'

import { ReactTitle } from '../../utils/MetaTags'
import palette from '../../../themes/palette'

interface ErrorPageProps {
  /** Large status code shown as the hero element, e.g. '404' or '500'. */
  code: string
  /** Short, human headline. */
  title: string
  /** Supportive sentence explaining what happened / what to do next. */
  message: string
  /** Text used for the browser tab / document title. Defaults to `title`. */
  metaTitle?: string
  /**
   * Use full-page anchor links instead of client-side router links.
   * Preferred for the 500 boundary, where the app state may be corrupt and a
   * clean reload is safer than an in-app transition.
   */
  hardLinks?: boolean
  /** Show a "Try again" button that reloads the current page (500 case). */
  showReload?: boolean
}

const HOME_PATH = '/'
const CONVERSATIONS_PATH = '/conversations'

/**
 * Branded, centered error layout shared by the 404 (not found) and 500
 * (error boundary) states. Renders under the standard OOH header/footer so it
 * feels like part of the app rather than a dead end.
 */
const ErrorPage = ({ code, title, message, metaTitle, hardLinks = false, showReload = false }: ErrorPageProps) => {
  const classes = useStyles()

  // A healthy app can transition in-place; a crashed app is safer reloading.
  const HomeButton = hardLinks ? (
    <a href={HOME_PATH} className={classes.buttonLink}>
      <Button variant='outlined' className='link' fullWidth>
        Back to home
      </Button>
    </a>
  ) : (
    <Link to={HOME_PATH} className={classes.buttonLink}>
      <Button variant='outlined' className='link' fullWidth>
        Back to home
      </Button>
    </Link>
  )

  const ConversationsButton = hardLinks ? (
    <a href={CONVERSATIONS_PATH} className={classes.buttonLink}>
      <Button variant='contained' className='link' fullWidth disableElevation>
        Browse conversations
      </Button>
    </a>
  ) : (
    <Link to={CONVERSATIONS_PATH} className={classes.buttonLink}>
      <Button variant='contained' className='link' fullWidth disableElevation>
        Browse conversations
      </Button>
    </Link>
  )

  return (
    <Container maxWidth='sm' className={classes.root}>
      <ReactTitle title={metaTitle || title} />

      <Typography variant='h1' component='p' className={classes.code} aria-hidden='true'>
        {code}
      </Typography>

      <Typography variant='h4' component='h1' className={classes.title}>
        {title}
      </Typography>

      <Typography variant='body1' className={classes.message}>
        {message}
      </Typography>

      <div className={classes.actions}>
        {ConversationsButton}
        {HomeButton}
        {showReload && (
          <Button variant='text' className={classes.reload} onClick={() => window.location.reload()}>
            Try again
          </Button>
        )}
      </div>
    </Container>
  )
}

export default ErrorPage

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      textAlign: 'center',
      padding: '4rem 1.5rem 5rem',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    },
    code: {
      color: palette.blue500,
      fontWeight: 700,
      lineHeight: 1,
      letterSpacing: '-2px',
      marginBottom: '0.5rem',
      [theme.breakpoints.down('xs')]: {
        fontSize: '3rem'
      }
    },
    title: {
      color: palette.grey800,
      marginBottom: '0.75rem'
    },
    message: {
      color: palette.grey600,
      maxWidth: '30rem',
      marginBottom: '2rem'
    },
    actions: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: '0.75rem',
      width: '100%',
      maxWidth: '20rem',
      [theme.breakpoints.up('sm')]: {
        flexDirection: 'row',
        justifyContent: 'center',
        maxWidth: 'none'
      }
    },
    buttonLink: {
      textDecoration: 'none',
      display: 'block',
      [theme.breakpoints.up('sm')]: {
        display: 'inline-block'
      }
    },
    reload: {
      color: palette.grey600,
      [theme.breakpoints.up('sm')]: {
        marginLeft: '0.25rem'
      }
    }
  })
)
